<script setup lang="ts">
import useBackend from '@/composition/useBackend';
import { DropdownButton, DropdownDivider, DropdownItem, Expandable, Tree, TreeNode } from "@hantera/design-system";
import { computed, ref, watch, watchEffect } from "vue";
import { type PhraseTemplate } from "@/backend/phrases";
import { Authorize, useContext, useDialogs } from '@hantera/portal-app';
import PhraseTranslation from "@/views/Settings/PhraseTranslation.vue";
import NewLanguage from './NewLanguage.vue';

const { phrases, user } = useBackend();
const dialogs = useDialogs();
const context = useContext()

watchEffect(() => {
  if (context.currentView.value?.name) {
    context.currentView.value.name.value = 'Portal Phrases'
  }
})

const nodes = ref<TreeNode[]>([]);

const selectedContext = ref<string>();

const renderedPhrases = computed<{
  hash: number,
  phrase: PhraseTemplate
}[]>(() => Object.keys(phrases.value)
  .map(Number)
  .map(hash => ({
    hash,
    phrase: phrases.value[hash]
  }))
  .filter(p => p.phrase.context === selectedContext.value)
  .sort((a, b) => {
    const aValue = a.phrase.template.trim().toLocaleLowerCase();
    const bValue = b.phrase.template.trim().toLocaleLowerCase();
    if (aValue.length != bValue.length) {
      return aValue.length < bValue.length ? -1 : 1;
    }
    return aValue < bValue ? -1 : 1
  })
);

watch(phrases.value, () => {

  const result: TreeNode[] = [];

  for (const key of Object.keys(phrases.value)) {
    const phrase = phrases.value[Number(key)]
    let path: string[] = ['global'];
    if (phrase.context !== '') {
      path = phrase.context.split('.');
    }

    let node: TreeNode | undefined;
    let currentNodeContext = '';
    for (const part of path) {

      currentNodeContext += (currentNodeContext.length > 0 ? '.' : '') + part;

      if (!node) {
        node = result.find(r => r.id === currentNodeContext)
        if (!node) {
          node = createNode();
          result.push(node);
        }
      } else {
        const parent = node as TreeNode;
        if (!parent.children) {
          parent.children = [];
        }

        node = parent.children.find((r: TreeNode) => r.id === currentNodeContext);
        if (!node) {
          node = createNode();
          parent.children.push(node);
        }
      }

      function createNode() {
        let capturedPath = currentNodeContext;
        return {
          id: currentNodeContext,
          label: part.replace('_', ' '),
          isSelected: () => selectedContext.value === capturedPath,
          onClick: () => {
            selectedContext.value = capturedPath;
          }
        };
      }
    }
  }

  nodes.value = result;
}, {
  deep: true,
  immediate: true
});

const knownLanguages = computed(() => {

  return Object.keys(Object.values(phrases.value).reduce((res, p) => {
    for (const key of Object.keys(p.translations ?? {})) {
      res[key] = true;
    }
    return res;
  }, {} as Record<string, boolean>));

});

function newLanguage(phrase: PhraseTemplate) {

  dialogs.openModal(
    "Specify Language",
    NewLanguage,
    true,
    {
      phrase
    }
  );

}

function addTranslation(phrase: PhraseTemplate, lang: string) {
  phrase.translations[lang.toLowerCase()] = ''
}

</script>

<template>

  <Authorize :patterns="['portal/settings/phrases:read']">
    <div class="phrases-view">
      <div class="phrases-view__tree neat-scroll">
        <Tree
            :clickable-parents="true"
            cache-key="phrases-view"
            :nodes="nodes"
            ref="tree"
        />
      </div>
      
      <div class="phrases-view__content neat-scroll">
        <div
          class="box-content p"
          v-for="(item) in renderedPhrases"
          :key="item.hash"
        >
          <Expandable>
            <template v-slot:label>
              {{ item.phrase.template }}
              <span
                v-for="(_, lang) in item.phrase.translations"
                :key="lang"
                class="phrases-view__lang-hint"
              >
                {{ lang }}
              </span>
            </template>
            <div class="phrases-view__translations-container">
              <table
                  class="phrases-view__translations"
                  v-if="Object.keys(item.phrase.translations).length > 0"
              >
                <PhraseTranslation
                    v-for="(translation, lang) in item.phrase.translations"
                    :key="lang"
                    :phrase-hash="item.hash"
                    :lang="lang"
                    :template="item.phrase.template"
                    :translation="translation"
                    :context="item.phrase.context"
                />
              </table>
              
              <div class="phrases-view__template-actions" v-if="user.authorizeSettingsPhrases('write')">
                <DropdownButton label="Add Translation">
                  <DropdownItem
                    v-for="lang in knownLanguages.filter(r => Object.keys(item.phrase.translations).indexOf(r) === -1)"
                    @click="addTranslation(item.phrase, lang)"
                  >
                    {{ lang.toUpperCase() }}
                  </DropdownItem>
                  <DropdownDivider v-if="knownLanguages.length > 0" />
                  <DropdownItem
                    @click="newLanguage(item.phrase)"
                  >
                    New language..
                  </DropdownItem>
                </DropdownButton>
              </div>
            </div>
          </Expandable>
        </div>
        
      </div>
  
    </div>
  </Authorize>
  
</template>

<style>
.phrases-view {
  display: grid;
  grid-template-columns: 250px auto;
  height: 100%;
}

.phrases-view__tree {
  overflow: auto;
}

.phrases-view__tree .tree-node__label {
  text-transform: capitalize;
}

.phrases-view__content {
  background-color: hsl(var(--color-100));
  border-left: 1px solid hsl(var(--color-70));
  display: flex;
  flex-direction: column;
  padding: 10px;
  gap: 10px;
  overflow: auto;
}

.phrases-view__lang-hint {
  text-transform: uppercase;
  color: hsl(var(--color-40));
  margin: 0 .6em;
}

.phrases-view__template-actions {
  display: flex;
  justify-content: flex-end;
  padding: 3px;
}

.phrases-view__translations-container {
  border-top: 1px solid hsl(var(--color-70));
  padding-top: 3px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.phrases-view__translations {
  width: 100%;
}
</style>